import { authStoreUserTournamentId } from 'modules/auth/store/selectors';
import { SportType } from 'modules/core/domain/enums/sport-type';
import { useOrganizer } from 'modules/organizer/ui/hooks/use-organizer';
import { Team } from 'modules/teams/domain/interfaces/team';
import { deleteTeam as deleteTeamAction } from 'modules/teams/store/teams';
import { ListTeamsArgs, useListTeamsQuery } from 'modules/teams/store/teams-api';
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { usePagedList } from 'shared/hooks/paged-list';
import { AppDispatch } from 'store/store';

/** Ключ localStorage для `perPage`: размер страницы запоминается между визитами. */
const STORAGE_KEY = 'teams';
const DEFAULT_PER_PAGE = 10;

/**
 * Стабильная ссылка обязательна: `usePagedList` считает по аргументам ключ скоупа,
 * а безымянная стрелка меняла бы идентичность на каждом рендере.
 */
const mapSearchToArgs = (search: string): Partial<ListTeamsArgs> => ({ namePart: search || undefined });

/**
 * Список команд организации: страница, размер страницы и поиск живут в URL,
 * данные — в RTK Query.
 *
 * Раньше список копился в redux-слайсе: страница дозагружала «следующие» команды
 * в общий массив и резала его на странице клиентом, из-за чего `?page=7` на
 * холодный старт откатывался на первую страницу. Теперь запрашивается ровно одна
 * страница, а число страниц выводится приёмом `limit + 1` (см. `usePagedList`).
 */
export const useTeamList = () => {
  const dispatch = useDispatch<AppDispatch>();
  const tournamentId = useSelector(authStoreUserTournamentId);
  const { info, isLoaded: isOrganizerLoaded } = useOrganizer();

  /**
   * Вид спорта берём только когда он действительно известен: пока организации нет,
   * подставленный «футбол» дал бы нефутбольной организации чужой список, который
   * через мгновение молча подменился бы правильным — и стоил бы лишнего запроса:
   * до ответа организации и после него это разные аргументы, то есть разные ключи
   * кэша RTK Query.
   */
  const sportType =
    info?.sportType ??
    // Организация загружена, но её нет (отказ запроса) — дальше ждать нечего.
    (isOrganizerLoaded ? SportType.football : undefined);

  const isWaitingForScope = !tournamentId || !sportType;

  const baseArgs = useMemo(
    () => ({ tournamentId: tournamentId as number, sportType: sportType as string }),
    [tournamentId, sportType],
  );

  const list = usePagedList<Team, ListTeamsArgs>({
    useQueryHook: useListTeamsQuery,
    baseArgs,
    mapSearchToArgs,
    storageKey: STORAGE_KEY,
    defaultPerPage: DEFAULT_PER_PAGE,
    // Пока неизвестны организация и вид спорта, запрос ушёл бы за чужими командами.
    skip: isWaitingForScope,
    isPending: isWaitingForScope,
  });

  const deleteTeam = useCallback(
    (id: number) => {
      // Мутация осталась танком; список обновит инвалидация тега внутри неё.
      return dispatch(deleteTeamAction({ id })).unwrap();
    },
    [dispatch],
  );

  return { ...list, deleteTeam };
};
